import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import agent from "../../app/api/agent";
import { AtdDiagnostics } from "../../app/models/Atd";
import { useStore } from "../../app/stores/store";
import { FEATURES } from "../../app/terminology/features";
import DashboardOverview from "./DashboardOverview";
import DashboardSetupCard, { DashboardSetupStatus } from "./DashboardSetupCard";
import useDashboardSetup from "./useDashboardSetup";
import { useLegacyMode } from "../legacy/LegacyModeContext";
import { getLatestDashboardFeatureAnnouncement } from "./dashboardFeatureAnnouncements";
import vcdbReferenceCalloutImage from "../../assets/dashboard/polaris-vcdb-reference.png";

type DashboardMetricProps = {
    label: string;
    value: string | number;
    tone?: "subdued" | "critical";
    onClick?: () => void;
};

const formatDateTime = (value?: string) => {
    if (!value) return "Not synced yet";

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "Not synced yet";

    return parsed.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const storeLabel = (value?: string | null) => value?.trim().replace(/\.myshopify\.com$/i, "") ?? "";

const formatCount = (value?: number) => (value ?? 0).toLocaleString();

const atdLoadingCardDelayMs = 500;
const showAtdDashboardCard = false;

const DashboardMetric = ({ label, value, tone, onClick }: DashboardMetricProps) => {
    const content = (
        <s-stack gap="small-200">
            <s-text color="subdued">{label}</s-text>
            <s-text fontSize="large-100" fontWeight="bold" tone={tone === "critical" ? "critical" : "auto"}>
                {value}
            </s-text>
        </s-stack>
    );

    if (onClick) {
        return (
            <s-clickable onClick={onClick} border="base" borderRadius="base" padding="base">
                {content}
            </s-clickable>
        );
    }

    return (
        <s-box border="base" borderRadius="base" padding="base">
            {content}
        </s-box>
    );
};

const Dashboard = () => {
    const navigate = useNavigate();
    const setup = useDashboardSetup();
    const { userStore } = useStore();
    const [atdDiagnostics, setAtdDiagnostics] = useState<AtdDiagnostics | undefined>();
    const [atdLoading, setAtdLoading] = useState(true);
    const [showAtdLoadingCard, setShowAtdLoadingCard] = useState(false);
    const [atdError, setAtdError] = useState<string | undefined>();
    const [vcdbReferenceCalloutLoaded, setVcdbReferenceCalloutLoaded] = useState(false);
    const { isLegacy } = useLegacyMode();
    const [showVcdbReferenceCallout, setShowVcdbReferenceCallout] = useState(false);
    const [dismissedFeatureAnnouncementId, setDismissedFeatureAnnouncementId] = useState("");

    useEffect(() => {
        if (!userStore.user) {
            userStore.getUser();
        }
    }, [userStore, userStore.user]);

    useEffect(() => {
        let cancelled = false;

        const loadDashboardSettings = async () => {
            try {
                const settings = await agent.Settings.dashboard();
                if (!cancelled) {
                    setShowVcdbReferenceCallout(!settings.vcdbReferenceCalloutDismissed);
                    setDismissedFeatureAnnouncementId(settings.dismissedFeatureAnnouncementId || "");
                }
            } catch {
                // Keep the reference-data prompt visible if settings cannot load,
                // but only for the current render; persistence remains DB-backed.
                if (!cancelled) {
                    setShowVcdbReferenceCallout(true);
                }
            } finally {
                if (!cancelled) {
                    setVcdbReferenceCalloutLoaded(true);
                }
            }
        };

        loadDashboardSettings();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadAtdDiagnostics = async () => {
            if (!showAtdDashboardCard) {
                setAtdLoading(false);
                return;
            }

            try {
                setAtdLoading(true);
                setAtdError(undefined);
                const diagnostics = await agent.Atd.diagnostics();
                if (!cancelled) setAtdDiagnostics(diagnostics);
            } catch (error: any) {
                if (!cancelled) {
                    setAtdDiagnostics(undefined);
                    setAtdError(error?.message || "Unable to load ATD status.");
                }
            } finally {
                if (!cancelled) setAtdLoading(false);
            }
        };

        loadAtdDiagnostics();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!atdLoading) {
            setShowAtdLoadingCard(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setShowAtdLoadingCard(true);
        }, atdLoadingCardDelayMs);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [atdLoading]);

    const atdStatusLabel = atdLoading
        ? "Loading"
        : atdDiagnostics?.profileActive
            ? "Active"
            : atdDiagnostics?.profileConfigured
                ? "Configured, inactive"
                : "Not configured";
    const failedOrders = atdDiagnostics?.failedOrderCount ?? 0;
    const atdLinkedProductsPath = "/products?tab=atd&linkStatus=linked";
    const atdUnlinkedProductsPath = "/products?tab=atd&linkStatus=unlinked";
    const showAtdCard = showAtdDashboardCard && (Boolean(atdDiagnostics?.profileConfigured) || Boolean(atdError) || (atdLoading && showAtdLoadingCard));
    const activeFeatureAnnouncement = vcdbReferenceCalloutLoaded
        ? getLatestDashboardFeatureAnnouncement(dismissedFeatureAnnouncementId)
        : undefined;
    // This replaces the old stale-inventory count with an action-oriented
    // catalog health signal: products linked in Shopify vs products currently
    // available from ATD's latest catalog sync.
    const linkedAvailableProducts = atdLoading
        ? "-"
        : `${formatCount(atdDiagnostics?.linkedProductCount)} / ${formatCount(atdDiagnostics?.availableProductCount ?? atdDiagnostics?.productCount)}`;
    const dismissVcdbReferenceCallout = async () => {
        setShowVcdbReferenceCallout(false);

        try {
            await agent.Settings.dismissVcdbReferenceCallout();
        } catch {
            // Leave it hidden for the active session; a later page load will
            // show it again if the DB save did not complete.
        }
    };
    const dismissFeatureAnnouncement = async () => {
        if (!activeFeatureAnnouncement) return;

        const announcementId = activeFeatureAnnouncement.id;
        setDismissedFeatureAnnouncementId(announcementId);

        try {
            await agent.Settings.dismissFeatureAnnouncement(announcementId);
        } catch {
            // Keep the announcement hidden for the active session. If the
            // setting did not persist, it can be shown again on a later load.
        }
    };
    return (
        <s-page heading={FEATURES.dashboard}>
            <s-button slot="primary-action" variant="primary" icon="import" onClick={() => navigate("/imports")}>
                Import fitment
            </s-button>
            <DashboardSetupStatus setup={setup} />

            <s-stack gap="base">
                <s-stack gap="small-300">
                    <s-heading fontSize="large-200">{storeLabel(userStore.user?.shopName) || storeLabel(userStore.user?.shopDomain) || "PartFits"}</s-heading>
                    <s-text color="subdued">Your fitment catalog and storefront activity at a glance.</s-text>
                </s-stack>

                <DashboardSetupCard setup={setup} />
                <DashboardOverview />

                {/* One promotion at a time: the newer announcement goes first. */}
                            {vcdbReferenceCalloutLoaded && showVcdbReferenceCallout && !isLegacy && !activeFeatureAnnouncement ? (
                                <s-section accessibilityLabel="Vehicle reference data">
                                    <s-query-container>
                                        <s-grid gridTemplateColumns="@container (inline-size > 560px) 1fr auto, 1fr" gap="large" alignItems="center">
                                            <s-stack gap="base">
                                                <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                                                    <s-heading>Vehicle reference data</s-heading>
                                                    <s-button
                                                        variant="tertiary"
                                                        icon="x"
                                                        accessibilityLabel="Dismiss reference data callout"
                                                        onClick={dismissVcdbReferenceCallout}
                                                    />
                                                </s-grid>
                                                <s-paragraph>
                                                    Add standard vehicle values to your admin selectors. This is off by default, so selectors only show vehicles in your fitment data.
                                                </s-paragraph>
                                                <s-stack direction="inline">
                                                    <s-button onClick={() => navigate("/storefront?section=reference-data")}>
                                                        Review reference data
                                                    </s-button>
                                                </s-stack>
                                            </s-stack>
                                            <s-box maxInlineSize="120px">
                                                <s-image src={vcdbReferenceCalloutImage} alt="" accessibilityRole="presentation" />
                                            </s-box>
                                        </s-grid>
                                    </s-query-container>
                                </s-section>
                            ) : null}

                            {showAtdCard ? (
                                <s-section accessibilityLabel="ATD integration">
                                    <s-stack gap="base">
                                        <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                                            <s-stack direction="inline" gap="base" alignItems="center">
                                                <s-heading>ATD integration</s-heading>
                                                <s-text color="subdued">{atdStatusLabel}</s-text>
                                            </s-stack>
                                            <s-stack direction="inline" gap="small-200">
                                                <s-button onClick={() => navigate("/integrations/atd/orders")}>View orders</s-button>
                                                <s-button onClick={() => navigate("/integrations/atd/profile")}>Settings</s-button>
                                            </s-stack>
                                        </s-grid>
                                        {atdError ? (
                                            <s-banner tone="critical">{atdError}</s-banner>
                                        ) : atdLoading ? (
                                            <s-text color="subdued">Loading ATD integration status...</s-text>
                                        ) : null}

                                        <s-query-container>
                                            <s-grid gridTemplateColumns="@container (inline-size > 640px) 1fr 1fr 1fr 1fr, 1fr 1fr" gap="small-200">
                                                <DashboardMetric
                                                    label="Products linked to ATD"
                                                    value={linkedAvailableProducts}
                                                    onClick={() => navigate(atdLinkedProductsPath)}
                                                />
                                                <DashboardMetric
                                                    label="ATD orders in progress"
                                                    value={atdLoading ? "-" : atdDiagnostics?.inProgressOrderCount ?? 0}
                                                />
                                                <DashboardMetric
                                                    label="ATD orders, last 30 days"
                                                    value={atdLoading ? "-" : atdDiagnostics?.ordersLast30Days ?? 0}
                                                />
                                                <DashboardMetric
                                                    label="Orders needing attention"
                                                    value={atdLoading ? "-" : failedOrders}
                                                    tone={failedOrders > 0 ? "critical" : undefined}
                                                    onClick={() => navigate("/integrations/atd/orders?status=NeedsAttention")}
                                                />
                                            </s-grid>
                                        </s-query-container>

                                        <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
                                            <s-text color="subdued">
                                                Last ATD catalog sync: {formatDateTime(atdDiagnostics?.lastCatalogSyncAt)}
                                            </s-text>
                                            {atdDiagnostics?.stragglerProductCount ? (
                                                <s-button onClick={() => navigate(atdUnlinkedProductsPath)}>
                                                    {`Review ${formatCount(atdDiagnostics.stragglerProductCount)} unlinked product${atdDiagnostics.stragglerProductCount === 1 ? "" : "s"}`}
                                                </s-button>
                                            ) : null}
                                        </s-stack>
                                    </s-stack>
                                </s-section>
                            ) : null}

                            {activeFeatureAnnouncement ? (
                                <s-section accessibilityLabel={activeFeatureAnnouncement.title}>
                                    <s-stack gap="base">
                                        <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                                            <s-heading>{activeFeatureAnnouncement.title}</s-heading>
                                            <s-button
                                                variant="tertiary"
                                                icon="x"
                                                accessibilityLabel="Dismiss feature announcement"
                                                onClick={dismissFeatureAnnouncement}
                                            />
                                        </s-grid>
                                        <s-paragraph>{activeFeatureAnnouncement.description}</s-paragraph>
                                        <s-stack direction="inline">
                                            <s-button onClick={() => navigate(activeFeatureAnnouncement.actionPath)}>
                                                {activeFeatureAnnouncement.actionLabel}
                                            </s-button>
                                        </s-stack>
                                    </s-stack>
                                </s-section>
                            ) : null}

                <s-text color="subdued">&copy; PartFits {new Date().getFullYear()}</s-text>
            </s-stack>
        </s-page>
    );
};

export default observer(Dashboard);
